import { getCurrentUser, signInWithRedirect, type SignInWithRedirectInput } from 'aws-amplify/auth';
import { FC, useContext, useEffect } from 'react';
import AppContext from 'src/AppContext';
import {
    applyCognitoClientOverride,
} from 'src/auth/cognitoClientOverride';
import { useLoginLocation } from 'src/customHooks/useLoginLocation';
import { ROUTES, ROUTE_SEGMENTS } from 'src/routes';
import LoadingPage from './LoadingPage';

const PARTNER_PORTAL_ENTRY_PATH = `/${ROUTE_SEGMENTS.services}/${ROUTE_SEGMENTS.partnerPortal}`;

const getSafeRedirectTarget = (target: string): string => {
    if (target === ROUTES.login || !target.startsWith('/') || target.startsWith('//')) {
        return ROUTES.start;
    }

    return target;
};

const isPartnerPortalPath = (path: string): boolean => path === PARTNER_PORTAL_ENTRY_PATH
    || path.startsWith(`${PARTNER_PORTAL_ENTRY_PATH}?`)
    || path.startsWith(`${PARTNER_PORTAL_ENTRY_PATH}/`);

export interface LoginProps {
};

const Login: FC<LoginProps> = () => {
    const context = useContext(AppContext);

    const location = useLoginLocation();
    const searchParams = new URLSearchParams(location.search);

    const requestedTarget = typeof location.state?.redirectTo === 'string'
        ? location.state.redirectTo
        : (searchParams.get('redirectTo') ?? ROUTES.start);
    const redirectTarget = getSafeRedirectTarget(requestedTarget);

    const isEntraOnlyLogin = context.featureFlags.usePartnerPortalDedicatedCognitoClient === false;

    const shouldUsePartnerPortalDedicatedClientFallback = isPartnerPortalPath(redirectTarget)
        && !!context.featureFlags.usePartnerPortalDedicatedCognitoClient
        && !!context.partnerPortalIdentityProviderName
        && !!context.partnerPortalUserPoolWebClientId;

    const fallbackIdentityProvider = shouldUsePartnerPortalDedicatedClientFallback ? context.partnerPortalIdentityProviderName : undefined;
    const fallbackClientId = shouldUsePartnerPortalDedicatedClientFallback ? context.partnerPortalUserPoolWebClientId : undefined;

    // Entra-only mode intentionally ignores provider/clientId passed via router state.
    const provider = isEntraOnlyLogin
        ? context.partnerPortalIdentityProviderName
        : location.state?.provider ?? fallbackIdentityProvider;
    const clientId = isEntraOnlyLogin
        ? undefined
        : location.state?.clientId ?? fallbackClientId;

    useEffect(() => {
        const doLogin = async () => {
            if (!clientId) {
                try {
                    await getCurrentUser();
                    window.location.href = redirectTarget;
                    return;
                } catch {
                    // No existing session: continue with redirect login.
                }
            }

            const input: SignInWithRedirectInput = {
                customState: JSON.stringify({ redirectTo: redirectTarget }),
                // A custom provider skips the Cognito hosted UI and redirects straight to Entra.
                ...(provider ? { provider: { custom: provider } } : {}),
            };

            applyCognitoClientOverride(clientId);
            await signInWithRedirect(input);
        };

        void doLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <LoadingPage />;
};

export default Login;